import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import {Link, useSearchParams} from 'react-router-dom';
import {QuestionCircleFilled} from '@ant-design/icons';
import { Transition } from 'react-transition-group';
import {
  StyledVendorsPageContainer,
  StyledVendorsPageContent,
  StyledDeleteVendorModalContent
} from './StyledVendorsPage';
import DeleteModal from '../../components/DeleteModal';
import SpinSmall from '../../components/SpinSmall';
import WebSocket from '../../components/WebSocket';
import DashboardPageTransition from '../../components/animations/DashboardPageTransition';
import PageDocumentDetails from '../../components/PageDocumentDetails/PageDocumentDetails';
import BalancePageHeader from '../../components/pages/TransactionsPage/BalancePageHeader/BalancePageHeader';
import AuthenticationWindow from '../../components/pages/CardsPage/AuthenticationWindow';
import {alertActions} from '../../state/actions/alert.actions';
import {vendorsActions} from '../../state/actions/vendors.actions';
import {
  backendWebsocketActionsConstants,
  DEFAULT_MODAL_PROPS,
  DEFAULT_PAGE_SIZE,
  SCAActionsConstants,
  SYSTEM_ANIMATION_DURATION,
  vendorStatusConstants
} from '../../constants';
import {
  objectHelpers,
  scaHelpers,
  systemHelpers,
  tabHelpers,
  tableHelpers,
  transactionsHelpers,
} from '../../utils/helpers';
import {firebaseEvents} from '../../snippets/firebase';
import routes from '../../routes/routes.json';
import VendorDetails from '../../components/pages/VendorsPage/VendorDetails';
import VendorEditor from '../../components/pages/VendorsPage/VendorEditor';
import VendorsTable from '../../components/pages/VendorsPage/VendorsTable';
import {vendorsPageConstants} from './vendorsPage.constants';
import {useTableState} from '../../hooks';

const {logEvent} = systemHelpers;

const {SELECTED_VENDOR_ID} = vendorsPageConstants;

const {VENDOR_UPDATE_STATUS_ACTION, VENDOR_DELETE_ACTION} = SCAActionsConstants;

const DEFAULT_TABLE_QUERY = {
  order_by: 'name',
  per_page: DEFAULT_PAGE_SIZE,
}

const {details: detailsStyles, table: tableStyles} = transactionsHelpers.getAnimationStyles({duration: SYSTEM_ANIMATION_DURATION, detailsMaxWidth: 572});

const removeSelectedKeysFromObject = (obj) => objectHelpers.removeKeysFromObject(obj, [SELECTED_VENDOR_ID])

const VendorsPage = () => {
  const [t] = useTranslation(['main', 'vendors']);
  const [activeAuthAction, setActiveAuthAction] = useState(null);
  const [addVendorModalProps, setAddVendorModalProps] = useState({mode: 'add', open: false});
  const [deleteModalProps, setDeleteModalProps] = useState(DEFAULT_MODAL_PROPS);
  const [authWindowProps, setAuthWindowProps] = useState({open: false});
  const [isOpenDetails, setIsOpenDetails] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [tempData, setTempData] = useState({
    data: undefined,
    id: undefined,
    errorCallbackFunction: undefined,
    successCallbackFunction: undefined,
  });
  const dispatch = useDispatch();

  const {
    tableData, setTableData,
    tableQuery, setTableQuery,
    loading, setLoading,
    finishTableLoading
  } = useTableState();

  const detailsNodeRef = useRef(null);
  const tableNodeRef = useRef(null);

  const handleOnPaginate = (activePage) => tableHelpers.handleOnPaginate({activePage, loadData, tableData, setTableData});

  const authModalProps = useMemo(() => {
    const isVendorUpdateStatus = activeAuthAction === VENDOR_UPDATE_STATUS_ACTION;

    let title = '';

    if (isVendorUpdateStatus) {
      title = `${t('change')} ${t('vendor')} ${t('status')}`;
    }

    return { title };
  }, [activeAuthAction, t]);

  useEffect(() => {
    const query = removeSelectedKeysFromObject(
      objectHelpers.getSearchParams(searchParams)
    );

    setTableQuery(query);
    loadData({ query, initial: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedVendor !== null) return;

    const selectedVendorId = searchParams.get(SELECTED_VENDOR_ID);
    if (!selectedVendorId || selectedVendor?.vendor_id === selectedVendorId) return;

    const vendor = (tableData?.rows || []).find(i => i.vendor_id === selectedVendorId);

    if (vendor) {
      handleOnRowClick(undefined, vendor);
    } else if (tableData.loaded) {
      dispatch(vendorsActions.getVendor({
        id: selectedVendorId,
        successCallback: (vendor) => setTableData({...tableData, rows: [vendor, ...tableData.rows]}),
        errorCallback: () => {
          const currentParams = objectHelpers.getSearchParams(searchParams);
          const updatedParams = { ...currentParams, [SELECTED_VENDOR_ID]: '' };

          setSearchParams(objectHelpers.omitInvalidValues(updatedParams));
        }
      }));
    }
  }, [tableData?.rows]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = ({query = null, initial = false, updateCurrentPage = false}) => {
    if (!loading) setLoading(true);

    let requestQuery = {
      ...DEFAULT_TABLE_QUERY,
      ...query
    };

    if (initial) {
      requestQuery = {...requestQuery, cursor: undefined};
    }

    if (updateCurrentPage) {
      requestQuery = {...requestQuery, cursor: tableData.pageCursors[tableData.page - 1] || undefined};
    }

    dispatch(vendorsActions.getVendors({
      query: requestQuery,
      successCallback: (response) => {
        let updatedViewTableDate = tabHelpers.getUpdatedTableData({
          tableData,
          response,
          initial,
          updateCurrentPage,
        });
        setTableData(updatedViewTableDate);
        finishTableLoading();
      },
      errorCallback: finishTableLoading
    }));
  }

  const loadVendorData = (vendorId) => {
    if (!vendorId) return;

    dispatch(vendorsActions.getVendor({
      id: vendorId,
      successCallback: (vendor) => {
        const beneficiaryId = vendor?.beneficiary_id;
        setSelectedVendor(vendor);

        if (beneficiaryId) {
          dispatch(vendorsActions.getBeneficiary({
            id: beneficiaryId,
            successCallback: (beneficiary) => {
              setSelectedVendor({
                ...vendor,
                bic: beneficiary.bic,
                iban: beneficiary.clear_iban
              });
            }
          }));
        }
      }
    }));
  }

  const handleOnFilter = (incomingQuery) => {
    const currentParams = objectHelpers.getSearchParams(searchParams);
    let mergedQuery = {
      ...currentParams,
      ...incomingQuery
    };

    mergedQuery = objectHelpers.omitInvalidValues(mergedQuery);

    const cleanedQuery = removeSelectedKeysFromObject(mergedQuery);

    setSearchParams(mergedQuery);
    setTableQuery(cleanedQuery);
    loadData({ query: cleanedQuery, initial: true });
  };

  const handleChangeVendorStatus = ({domEvent, value, vendor}) => {
    domEvent.stopPropagation();
    const id = vendor?.beneficiary_id;
    if (!id) {
      alertActions.error(t('vendors:error.beneficiaryIsMissing'), 4000, 380);
      return;
    }

    const data = {is_active: parseInt(value) === vendorStatusConstants.ACTIVE};

    handleUpdateVendorStatus({id, data});
    setActiveAuthAction(VENDOR_UPDATE_STATUS_ACTION);
    setTempData({id, data});
  }

  const handleOnTableChange = (pagination, filters, sorter, extra) => {
    const currentParams = objectHelpers.getSearchParams(searchParams);
    let mergedQuery = {
      ...currentParams,
      ...filters,
      order_by: tableHelpers.getOrderByValue(sorter),
    };

    setSearchParams(objectHelpers.omitInvalidValues(mergedQuery));
    tabHelpers.handleOnTableChange({pagination, filters, sorter, extra, loadDataFunc: loadData})
  }

  const handleOnAddClick = () => setAddVendorModalProps({...addVendorModalProps, mode: 'add', open: true});

  const handleCancelAddVendorModal = () => setAddVendorModalProps({...addVendorModalProps, open: false});

  const handleSuccessVendorEdit = () => {
    const isEditMode = addVendorModalProps.mode === 'edit';
    const vendorId = selectedVendor?.vendor_id;

    if (isEditMode && !vendorId) return;

    handleCancelAddVendorModal();

    if (isEditMode) {
      loadVendorData(vendorId);
    } else {
      loadData({ initial: true });
    }
  }

  const handleUpdateVendorStatus = ({id, data} = {}) => {
    dispatch(vendorsActions.updateBeneficiary({
      id,
      data,
      headers: scaHelpers.getAuthHeaders(VENDOR_UPDATE_STATUS_ACTION),
      successCallback: () => {
        const status = data?.is_active ? vendorStatusConstants.ACTIVE : vendorStatusConstants.INACTIVE;
        setTableData({
          ...tableData,
          rows: tableData.rows.map(row => row.beneficiary_id === id ? { ...row, status } : row)
        });

        if (selectedVendor) {
          setSelectedVendor({...selectedVendor, status});
        }

        logEvent(firebaseEvents.VENDORS_UPDATE_STATUS);
      },
      errorCallback: (response) => {
        scaHelpers.SCAResponseCallback({
          response,
          scaCallback: (scaProps) => setAuthWindowProps({...authWindowProps, ...scaProps}),
          errorCallback: () => {
            const message = (
              <>
                {t('vendors:error.statusUpdate')}&nbsp;{t('submitTicketIfIssuePersists')}&nbsp;
                <Link to={routes.support}>
                  <QuestionCircleFilled /> {t('section')}
                </Link>
              </>
            );
            alertActions.warning(message, 6000, 520);
          }
        });
      }
    }));
  }

  const handleCancelAuthModal = () => {
    const operationActions = {
      [VENDOR_UPDATE_STATUS_ACTION]: () => {},
      [VENDOR_DELETE_ACTION]: () => setDeleteModalProps({...deleteModalProps, loading: false}),
    }
    setAuthWindowProps({...authWindowProps, open: false});
    operationActions[activeAuthAction]?.(tempData);
  }

  const handleOnSuccessAuthModal = () => {
    const operationActions = {
      [VENDOR_UPDATE_STATUS_ACTION]: handleUpdateVendorStatus,
      [VENDOR_DELETE_ACTION]: handleOkDeleteModal,
    }
    setAuthWindowProps({...authWindowProps, open: false});
    operationActions[activeAuthAction]?.(tempData);
  }

  const handleOnRowClick = (e, record) => {
    const vendorId = record?.vendor_id;
    if (!vendorId) return;

    loadVendorData(vendorId);

    setIsOpenDetails(true);
    setSelectedVendor(record);
    setSearchParams({
      ...objectHelpers.getSearchParams(searchParams),
      [SELECTED_VENDOR_ID]: vendorId
    });
  }

  const handleCloseDetails = () => {
    setIsOpenDetails(false);
    setSelectedVendor(null);

    const updatedSearchParams = removeSelectedKeysFromObject(objectHelpers.getSearchParams(searchParams));
    setSearchParams(updatedSearchParams);
  }

  const handleOpenEditModal = () => {
    setAddVendorModalProps({...addVendorModalProps, mode: 'edit', open: true});
  }

  const handleWebsocketMessage = (data) => {
    const getResponseProp = (key) => objectHelpers.getObjProp(data, key);
    const action = getResponseProp('action');

    if (!action) return;

    switch (action) {
      case backendWebsocketActionsConstants.VENDOR_CREATE_COMPLETED: {
        const vendorId = getResponseProp('vendor_id');

        // update table row
        setTableData({
          ...tableData,
          rows: tableData.rows.map(item => item.vendor_id === vendorId ? {...item, ...data} : item)
        });

        // load vendor details if vendor
        if (selectedVendor?.vendor_id === vendorId) {
          loadVendorData(vendorId);
        }
        break;
      }
      default:
        console.warn(`Unhandled websocket action: ${action}`);
    }
  };

  const handleCancelDeleteModal = () => {
    setDeleteModalProps({...deleteModalProps, ...DEFAULT_MODAL_PROPS});
  }

  const handleOkDeleteModal = () => {
    const id = selectedVendor?.vendor_id;
    if (!id) return;

    setActiveAuthAction(VENDOR_DELETE_ACTION);

    setDeleteModalProps({...deleteModalProps, loading: true});

    dispatch(vendorsActions.deleteVendor({
      id,
      headers: scaHelpers.getAuthHeaders(VENDOR_DELETE_ACTION),
      successCallback: () => {
        alertActions.success(t('vendors:successVendorDeleteMessage'));
        handleCancelDeleteModal();
        handleCloseDetails();
        loadData({ updateCurrentPage: true });
      },
      errorCallback: (response) => {
        scaHelpers.SCAResponseCallback({
          response,
          scaCallback: (scaProps) => setAuthWindowProps({...authWindowProps, ...scaProps}),
          errorCallback: () => alertActions.error(t('vendors:errorVendorDeleteMessage'))
        });
      }
    }));
  }

  const handleOpenDeleteModal = () => {
    setDeleteModalProps({...deleteModalProps, open: true});
  }

  return (
    <StyledVendorsPageContainer>
      <PageDocumentDetails title={t('pageTitles.vendorsList')} />
      <BalancePageHeader breadcrumbs={[{title: t('vendors')}]} />
      <DashboardPageTransition>
        <SpinSmall spinning={loading}>
          <StyledVendorsPageContent>
            <Transition nodeRef={tableNodeRef} in={isOpenDetails} timeout={SYSTEM_ANIMATION_DURATION}>
              {state => (
                <VendorsTable
                  loading={loading}
                  onAddClick={handleOnAddClick}
                  onFilter={handleOnFilter}
                  onPaginate={handleOnPaginate}
                  onTableChange={handleOnTableChange}
                  onRowClick={handleOnRowClick}
                  onStatusChange={handleChangeVendorStatus}
                  selectedRowIndex={selectedVendor?.vendor_id}
                  tableData={tableData}
                  tableQuery={tableQuery}
                  style={{
                    ...tableStyles.default,
                    ...tableStyles[state]
                  }}
                />
              )}
            </Transition>
          </StyledVendorsPageContent>
        </SpinSmall>
      </DashboardPageTransition>

      <Transition nodeRef={detailsNodeRef} in={isOpenDetails} timeout={SYSTEM_ANIMATION_DURATION}>
        {state => (
          <VendorDetails
            className='vendor-page__vendor-details'
            onBack={handleCloseDetails}
            onEdit={handleOpenEditModal}
            onDelete={handleOpenDeleteModal}
            onStatusChange={handleChangeVendorStatus}
            style={{
              ...detailsStyles.default,
              ...detailsStyles[state]
            }}
            vendor={selectedVendor}
          />
        )}
      </Transition>

      <VendorEditor
        {...addVendorModalProps}
        onCancel={handleCancelAddVendorModal}
        onSuccess={handleSuccessVendorEdit}
        vendorId={selectedVendor?.vendor_id}
      />

      <DeleteModal
        onCancel={handleCancelDeleteModal}
        onOk={handleOkDeleteModal}
        title={t(`vendors:modal.delete.title`)}
        {...deleteModalProps}
      >
        <StyledDeleteVendorModalContent>
          <p>{t(`vendors:modal.delete.descriptionTitle`)}</p>
          <p>{t(`vendors:modal.delete.descriptionText`)}</p>
        </StyledDeleteVendorModalContent>
      </DeleteModal>

      <AuthenticationWindow
        {...authWindowProps}
        authModalProps={authModalProps}
        handleCancel={handleCancelAuthModal}
        onSuccess={handleOnSuccessAuthModal}
        operationName={activeAuthAction}
      />

      <WebSocket onMessage={handleWebsocketMessage} />

    </StyledVendorsPageContainer>
  );
}

export default VendorsPage;
